import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const StaffTable = ({ staff, onViewSchedule, onEditAvailability, onEditProfile, onDeleteStaff }) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedStaff = [...staff]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];
    
    if (typeof aValue === 'string') {
      aValue = aValue?.toLowerCase();
      bValue = bValue?.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-success text-success-foreground';
      case 'inactive':
        return 'bg-error text-error-foreground';
      case 'on_leave':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-secondary text-secondary-foreground';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Manager':
        return 'bg-primary text-primary-foreground';
      case 'Staff':
        return 'bg-accent text-accent-foreground';
      case 'Receptionist':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="bg-background rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-2 p-0 h-auto font-medium"
                >
                  <span>{t("staff.columnEmployee")}</span>
                  <Icon 
                    name={sortField === 'name' && sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={16} 
                  />
                </Button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('role')}
                  className="flex items-center space-x-2 p-0 h-auto font-medium"
                >
                  <span>{t("staff.role")}</span>
                  <Icon 
                    name={sortField === 'role' && sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={16} 
                  />
                </Button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">{t("staff.columnContact")}</th>
              <th className="text-left p-4 font-medium text-foreground">
                <Button
                  variant="ghost"
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-2 p-0 h-auto font-medium"
                >
                  <span>{t("common.status")}</span>
                  <Icon 
                    name={sortField === 'status' && sortDirection === 'desc' ? 'ChevronDown' : 'ChevronUp'} 
                    size={16} 
                  />
                </Button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">{t("staff.columnServices")}</th>
              <th className="text-right p-4 font-medium text-foreground">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {sortedStaff?.map((member) => (
              <tr key={member?.id} className="border-t border-border hover:bg-muted/50 transition-colors">
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                      <Image
                        src={member?.avatar}
                        alt={member?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{member?.name}</div>
                      <div className="text-sm text-text-secondary">ID: {member?.id}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(member?.role)}`}>
                    {member?.role}
                  </span>
                </td>
                <td className="p-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2 text-sm text-foreground">
                      <Icon name="Mail" size={14} />
                      <span>{member?.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-text-secondary">
                      <Icon name="Phone" size={14} />
                      <span>{member?.phone}</span>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(member?.status)}`}>
                    {member?.status === 'active' ? t("staff.statusActive") : member?.status === 'inactive' ? t("staff.statusInactive") : t("staff.statusOnLeave")}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex flex-wrap gap-1">
                    {member?.services?.slice(0, 2)?.map((service, index) => (
                      <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-accent/10 text-accent-foreground">
                        {service}
                      </span>
                    ))}
                    {member?.services?.length > 2 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-muted text-muted-foreground">
                        +{member?.services?.length - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewSchedule(member)}
                      className="h-8 w-8"
                    >
                      <Icon name="Calendar" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditAvailability(member)}
                      className="h-8 w-8"
                    >
                      <Icon name="Clock" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditProfile(member)}
                      className="h-8 w-8"
                    >
                      <Icon name="Edit" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDeleteStaff(member)}
                      className="h-8 w-8 text-error hover:text-error"
                    >
                      <Icon name="Trash2" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StaffTable;